import { Extension } from '@tiptap/core';

import { NodeSelection, Plugin } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import { Editor } from '@tiptap/react';
import { setOpenAskAi } from '../openAskAiUtils';

export interface DragHandleOptions {
  /**
   * The width of the drag handle
   */
  dragHandleWidth: number;
  editor: Editor;
}
function absoluteRect(node: Element) {
  const data = node.getBoundingClientRect();
  const modal = node.closest('[role="dialog"]');

  if (modal && window.getComputedStyle(modal).transform !== 'none') {
    const modalRect = modal.getBoundingClientRect();

    return {
      top: data.top - modalRect.top,
      left: data.left - modalRect.left,
      width: data.width,
    };
  }

  return {
    top: data.top,
    left: data.left,
    width: data.width,
  };
}

function nodeDOMAtCoords(coords: { x: number; y: number }) {
  return document
    .elementsFromPoint(coords.x, coords.y)
    .find(
      (elem: Element) =>
        elem.parentElement?.matches?.('.ProseMirror') ||
        elem.matches(
          [
            'li',
            'p:not(:first-child)',
            'pre',
            'blockquote',
            'h1, h2, h3, h4, h5, h6',
          ].join(', '),
        ),
    );
}

function nodePosAtDOM(
  node: Element,
  view: EditorView,
  options: DragHandleOptions,
) {
  const boundingRect = node.getBoundingClientRect();

  return view.posAtCoords({
    left: boundingRect.left + 50 + options.dragHandleWidth,
    top: boundingRect.top + 1,
  });
}

function DragHandle(options: DragHandleOptions) {
  function clearHighlight() {
    options.editor.commands.unsetSelectionHighlight();
  }

  function handleDragStart(event: DragEvent, view: EditorView) {
    view.focus();

    if (!event.dataTransfer) return;

    const node = nodeDOMAtCoords({
      x: event.clientX + 50 + options.dragHandleWidth,
      y: event.clientY,
    });

    if (!(node instanceof Element)) return;

    const nodePos = nodePosAtDOM(node, view, options)?.inside;
    if (nodePos == null || nodePos < 0) return;

    view.dispatch(
      view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)),
    );

    const slice = view.state.selection.content();
    const { dom, text } = view.serializeForClipboard(slice);

    event.dataTransfer.clearData();
    event.dataTransfer.setData('text/plain', text);
    event.dataTransfer.setData('text/html', dom.outerHTML);

    event.dataTransfer.effectAllowed = 'copyMove';

    event.dataTransfer.setDragImage(node, 0, 0);

    view.dragging = { slice, move: event.ctrlKey };
  }

  function handleClick(event: MouseEvent, view: EditorView) {
    view.focus();

    view.dom.classList.remove('dragging');

    const node = nodeDOMAtCoords({
      x: event.clientX + 50 + options.dragHandleWidth,
      y: event.clientY,
    });

    if (!(node instanceof Element)) return;

    const nodePos = nodePosAtDOM(node, view, options)?.inside;
    if (nodePos == null || nodePos < 0) return;

    view.dispatch(
      view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)),
    );
    options.editor.commands.setSelectionHighlight('drag-handle');
  }

  function handleAskAiClick(event: MouseEvent, view: EditorView) {
    view.focus();

    const node = nodeDOMAtCoords({
      x: event.clientX + 50 + options.dragHandleWidth,
      y: event.clientY,
    });

    if (!(node instanceof Element)) return;

    const nodePos = nodePosAtDOM(node, view, options)?.inside;

    setOpenAskAi(view, { open: true, nodePos });
  }

  let nodeActionsElement: HTMLElement | null = null;
  let addHandleElement: HTMLElement | null = null;
  let askAiHandleElement: HTMLElement | null = null;
  let dragHandleElement: HTMLElement | null = null;

  function hideNodeActions() {
    if (nodeActionsElement) {
      nodeActionsElement.classList.add('hide');
    }
  }

  function showNodeActions() {
    if (nodeActionsElement) {
      nodeActionsElement.classList.remove('hide');
    }
  }

  return new Plugin({
    view: (view) => {
      // create drag handle
      dragHandleElement = document.createElement('div');
      dragHandleElement.draggable = true;
      dragHandleElement.dataset.dragHandle = '';
      dragHandleElement.classList.add('action', 'drag-handle');

      // set drag handle events
      dragHandleElement.addEventListener('dragstart', (e) => {
        handleDragStart(e, view);
      });
      dragHandleElement.addEventListener('click', (e) => {
        handleClick(e, view);
      });

      //create Ask ai handle
      askAiHandleElement = document.createElement('div');
      askAiHandleElement.classList.add('action', 'ask-ai-handle');

      askAiHandleElement.addEventListener('click', (e) => {
        handleAskAiClick(e, view);
      });
      // create add handle
      // addHandleElement = document.createElement('div');
      // addHandleElement.classList.add('action', 'add-handle');

      // set add handle events
      // addHandleElement.addEventListener('click', (e) => {
      //   const node = nodeDOMAtCoords({
      //     x: e.clientX + 50 + options.dragHandleWidth,
      //     y: e.clientY,
      //   });

      //   if (!(node instanceof Element)) return;

      //   const nodePos = nodePosAtDOM(node, view, options);
      //   if (!nodePos) return;

      //   const newLine = view.state.schema.nodes.paragraph.create();

      //   const resolvedPos = view.state.doc.resolve(nodePos.inside);

      //   const parentInfo = resolvedPos.parent.childAfter(resolvedPos.parentOffset);
      //   const linkNode = parentInfo.node;
      //   const linkStartPos = parentInfo.offset;
      //   const posInParent = resolvedPos.parentOffset;
      //   const offsetInLink = posInParent - linkStartPos;
      //   const linkFrom = nodePos.inside - offsetInLink;
      //   const linkTo = linkFrom + (linkNode?.nodeSize || 0);

      //   // Insert the new line after the clicked node
      //   const tr1 = view.state.tr.insert(linkTo, newLine);
      //   view.dispatch(tr1);

      //   const newPos = view.state.tr.doc.resolve(linkTo + 1);
      //   const tr2 = view.state.tr.setSelection(TextSelection.create(view.state.tr.doc, newPos.pos));

      //   view.focus();

      //   view.dispatch(tr2);
      // });

      // create node actions element
      nodeActionsElement = document.createElement('div');
      nodeActionsElement.classList.add('node-actions');

      // nodeActionsElement.appendChild(addHandleElement);
      nodeActionsElement.appendChild(askAiHandleElement);
      nodeActionsElement.appendChild(dragHandleElement);

      hideNodeActions();

      view?.dom?.parentElement?.appendChild(nodeActionsElement);

      return {
        destroy: () => {
          nodeActionsElement?.remove?.();
          nodeActionsElement = null;
        },
      };
    },
    props: {
      handleDOMEvents: {
        mousemove: (view, event) => {
          if (!view.editable) {
            return;
          }

          let node = nodeDOMAtCoords({
            x: event.clientX + 50 + options.dragHandleWidth,
            y: event.clientY,
          });

          if (!(node instanceof Element) || node.matches('ul, ol')) {
            hideNodeActions();
            return;
          }

          const draggable = node.closest('[contenteditable="false"]');
          if (draggable) {
            node = draggable;
          }

          const compStyle = window.getComputedStyle(node);
          const lineHeight = parseInt(compStyle.lineHeight, 10);
          const paddingTop = parseInt(compStyle.paddingTop, 10);

          const rect = absoluteRect(node);

          rect.top += (lineHeight - 24) / 2;
          rect.top += paddingTop;
          // Li markers
          if (node.matches('ul:not([data-type=taskList]) li, ol li')) {
            rect.left -= options.dragHandleWidth;
          }

          // get node actions width
          rect.width = (nodeActionsElement?.offsetWidth || 0) + 8;

          if (!nodeActionsElement) return;

          nodeActionsElement.style.left = `${rect.left - rect.width}px`;
          nodeActionsElement.style.top = `${rect.top}px`;
          showNodeActions();
        },
        keydown: () => {
          hideNodeActions();
          clearHighlight();
        },
        mousewheel: () => {
          hideNodeActions();
          clearHighlight();
        },
        click: () => {
          clearHighlight();
        },
        // dragging class is used for CSS
        dragstart: (view) => {
          view.dom.classList.add('dragging');
          clearHighlight();
        },
        drop: (view) => {
          view.dom.classList.remove('dragging');
          hideNodeActions();
          return false;
        },
        dragend: (view) => {
          view.dom.classList.remove('dragging');
          return false;
        },
      },
    },
  });
}

export const draggableNodeExtension = Extension.create({
  name: 'dragAndDrop',

  addProseMirrorPlugins() {
    return [
      DragHandle({
        dragHandleWidth: 24,
        editor: this.editor,
      }),
    ];
  },
});
